import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getPost, JOURNAL } from "../../data/journal";
import PlantImage from "../../components/fancyplants/PlantImage";
import { formatDate } from "../../utils/format";
import { EmptyState } from "../../components/fancyplants/states";
import Seo from "../../components/fancyplants/Seo";

export default function JournalPostPage() {
  const { slug } = useParams();
  const post = getPost(slug);
  useEffect(() => window.scrollTo(0, 0), [slug]);

  if (!post) {
    return (
      <div className="container-fp pt-32 pb-20">
        <Seo title="Post not found" noindex />
        <EmptyState
          title="Post not found"
          message="This entry may have moved or the link is mistyped."
          action={<Link to="/journal" className="btn-forest">Back to the Journal</Link>}
        />
      </div>
    );
  }

  const more = JOURNAL.filter((p) => p.slug !== slug).slice(0, 2);

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    image: post.cover,
    datePublished: post.date,
    description: post.excerpt,
    author: { "@type": "Organization", name: "Fancy Plants" },
  };

  return (
    <div className="pt-24">
      <Seo
        title={post.title}
        description={post.excerpt}
        path={`/journal/${post.slug}`}
        image={post.cover}
        type="article"
        jsonLd={articleLd}
      />
      <article className="container-fp py-8">
        <Link to="/journal" className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-forest-700">
          <ArrowLeft size={16} /> The Journal
        </Link>

        <div className="mx-auto mt-6 max-w-prose">
          <p className="text-sm text-ink-soft">{formatDate(post.date)} · {post.readingTime}</p>
          <h1 className="t-h1 mt-2">{post.title}</h1>
        </div>

        <div className="mx-auto my-8 max-w-3xl">
          <PlantImage src={post.cover} alt={post.title} ratio="2/1" className="rounded-3xl" priority />
        </div>

        <div className="mx-auto max-w-prose space-y-5 text-lg leading-relaxed text-ink">
          {post.body.map((para, i) => (
            <p key={i} className={i === 0 ? "first-letter:float-left first-letter:mr-2 first-letter:font-display first-letter:text-6xl first-letter:leading-[0.8] first-letter:text-brass" : ""}>
              {para}
            </p>
          ))}
        </div>
      </article>

      <section className="border-t border-line">
        <div className="container-fp py-12">
          <h2 className="t-h3 mb-6">Keep reading</h2>
          <div className="grid gap-6 sm:grid-cols-2">
            {more.map((p) => (
              <Link key={p.id} to={`/journal/${p.slug}`} className="group surface overflow-hidden hover:shadow-lift">
                <PlantImage src={p.cover} alt={p.title} ratio="2/1" />
                <div className="p-5">
                  <h3 className="font-display text-xl group-hover:text-forest-700">{p.title}</h3>
                  <p className="mt-1 text-sm text-ink-soft">{p.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
